'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useCreateProperty } from '@/hooks/use-properties'
import { PropertyForm } from '@/components/partials/properties'
import { type PropertyFormRef } from '@/components/partials/properties/form'
import { CreatePropertyParams } from '@/types/property'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const propertyFormRef = useRef<PropertyFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createProperty, isPending: isLoading } = useCreateProperty()

  const handleSubmit = (data: CreatePropertyParams) => {
    createProperty(data, {
      onSuccess: async newMessage => {
        setIsRedirecting(true)
        queryClient.setQueryData(['property', newMessage.id], newMessage)
        await queryClient.invalidateQueries({ queryKey: ['properties'] })
        toast.success(t('created_successfully'))
        router.push(`/products/properties/${newMessage.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreatePropertyParams) => {
    createProperty(data, {
      onSuccess: async newMessage => {
        queryClient.setQueryData(['property', newMessage.id], newMessage)
        await queryClient.invalidateQueries({ queryKey: ['properties'] })
        const detailUrl = `/products/properties/${newMessage.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        propertyFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (propertyFormRef.current) {
        propertyFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products/properties')
  }

  return (
    <PropertyForm
      key={formKey}
      ref={propertyFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
